import { useEffect, useState } from 'react'
import { useEventCallback } from 'studiokit-scaffolding-js/lib/hooks/useEventCallback'
import { useFetchData } from 'studiokit-scaffolding-js/lib/hooks/useFetchData'
import { ClientConfig } from '../types/ClientConfig'
import { FETCH_STATUS } from '../types/FetchStatus'

/**
 * Used until `GET /config` resolves, and if it fails. Keeps the UI from rendering a broken usage meter
 * or a zero-byte upload limit, and covers the help modal for a user who is not signed in. Values match
 * the API's own defaults.
 */
export const DEFAULT_CLIENT_CONFIG: ClientConfig = {
	maxUserTokenCount: 2000000,
	userTokenWindowSeconds: 60 * 60,
	maxFileSizeBytes: 20 * 1024 * 1024,
	maxPageCount: 75
}

/**
 * Loads the API's deploy-time limits once.
 *
 * @param isAuthenticated Whether there is an authenticated user. The endpoint is anonymous, but the
 * fetch layer always sends a token, so there is nothing to gain by asking earlier.
 */
export function useClientConfig(isAuthenticated: boolean) {
	const [clientConfig, setClientConfig] = useState<ClientConfig | undefined>()

	const { status, send: getClientConfig } = useFetchData('clientConfig', (data: unknown) => {
		setClientConfig(data as ClientConfig)
	})

	// automatically load once authenticated, if not set and not already fetching
	const requestClientConfig = useEventCallback(() => {
		if (isAuthenticated && clientConfig === undefined && status === FETCH_STATUS.READY) {
			getClientConfig()
		}
	})
	useEffect(() => {
		requestClientConfig()
	}, [isAuthenticated, requestClientConfig])

	return clientConfig ?? DEFAULT_CLIENT_CONFIG
}
